import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { getStreamedQuestionChunks } from "../modules/AiQuestionLoaderUtils";
import {
  useAddAiQuestionsToWorksheet,
  useUpdateAiGeneratedWsInstruction,
  useUpdateAiGeneratedQuestionsOrder,
} from "./useAddAiQuestionsToWorksheet";
import {
  QUESTION_END_TAG,
  AI_WORKSHEET_TAGS,
  QUESTION_START_TAG,
} from "../modules/AiQuestionLoaderConstant";

const STATES = {
  END: "END",
  SAVED: "SAVED",
  SAVING: "SAVING",
};

// * Verify if streamed question is completely streamed.
const isQuestionCompletelyStreamed = question => {
  const text = _.trim(_.get(question, "text", ""));
  const hasStartTag = _.startsWith(text, QUESTION_START_TAG);
  const hasEndTag = _.includes(text, QUESTION_END_TAG);
  return hasStartTag && hasEndTag;
};

const getWorksheetInstructionChunk = messages => {
  try {
    const content = _.get(_.last(messages), "node.content", "");
    const { INSTRUCTION_START_TAG, INSTRUCTION_END_TAG } = AI_WORKSHEET_TAGS;
    if (!_.includes(content, INSTRUCTION_END_TAG)) return "";
    let instructionText = _.split(content, INSTRUCTION_START_TAG);
    instructionText = _.split(_.last(instructionText), INSTRUCTION_END_TAG);
    return _.trim(_.first(instructionText));
  } catch {
    return "";
  }
};

/**
 *
 * @param {*} props
 * @returns Returns null
 * @description
 * - From streamed question text, create each question chunk
 * - Send to server to each question
 * - Once all question are saved to server, update question order
 * - Trigger onGenerationComplete callback
 */
const useAiQuestionSaveToServer = props => {
  const {
    messages,
    sections,
    stopTriggered,
    streamingState,
    descriptionField,
    onGenerationComplete,
    worksheetMutationData,
  } = props;
  const wsInstructionRef = useRef(false);
  const questionStatesRef = useRef({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [questionStates, setQuestionStates] = useState({});
  const [sequenceToQuestionId, setSequenceToQuestionId] = useState({});

  // * Custom hooks to Add & Order Questions and Update Worksheet instruction
  const addQuestionToWorksheet = useAddAiQuestionsToWorksheet(props);
  const updateAiQuestionsOrder = useUpdateAiGeneratedQuestionsOrder(props);
  const updateWorksheetInstruction = useUpdateAiGeneratedWsInstruction(props);

  // * Function to set questionId for each question sequence
  const setSequence = useCallback(
    (sequence, questionId) => {
      setSequenceToQuestionId(prev => ({ ...prev, [sequence]: questionId }));
    },
    [setSequenceToQuestionId]
  );

  // * Effect to update worksheet instruction in cache and server both
  useEffect(() => {
    if (wsInstructionRef.current || !_.isEmpty(descriptionField?.value)) return;
    const streamedWsInstruction = getWorksheetInstructionChunk(messages);
    if (_.isEmpty(streamedWsInstruction)) return;
    wsInstructionRef.current = true;
    updateWorksheetInstruction(streamedWsInstruction);
  }, [messages, descriptionField, updateWorksheetInstruction]);

  // * Effect to monitor messages and handle saving of completely streamed questions to server.
  useEffect(() => {
    const saveCompletedQuestionsToServer = async () => {
      const questions = getStreamedQuestionChunks(messages);

      _.forEach(questions, async (question, offset) => {
        if (!isQuestionCompletelyStreamed(question)) return;
        if (questionStatesRef?.current?.[offset]) return;
        const payload = {
          offset,
          aiSequence: [offset + 1],
          questionContent: [_.get(question, "text", "")],
        };

        questionStatesRef.current[offset] = STATES.SAVING;
        setQuestionStates(prev => ({ ...prev, [offset]: STATES.SAVING }));
        const questionId = await addQuestionToWorksheet(payload);
        questionStatesRef.current[offset] = STATES.SAVED;
        setQuestionStates(prev => ({ ...prev, [offset]: STATES.SAVED }));

        if (questionId) setSequence(offset + 1, questionId);
      });
    };

    setIsGenerating(true);
    saveCompletedQuestionsToServer();
  }, [messages, setSequence, setQuestionStates, addQuestionToWorksheet]);

  // * Effect to monitor question states and trigger completion callback when all questions are saved or when generation is stopped.
  useEffect(() => {
    const finishQuestionGeneration = async () => {
      await updateAiQuestionsOrder(sequenceToQuestionId);
      onGenerationComplete?.();
      setIsGenerating(false);
    };

    const allSaved =
      !_.isEmpty(questionStates) &&
      _.every(questionStates, state => state === STATES.SAVED);
    if (isGenerating && stopTriggered && allSaved) {
      // * When user manually stops the generation
      finishQuestionGeneration();
    } else if (isGenerating && streamingState === STATES.END && allSaved) {
      // * When all questions are streamed and saved successfully
      finishQuestionGeneration();
    }
  }, [
    sections,
    isGenerating,
    stopTriggered,
    questionStates,
    streamingState,
    questionStatesRef,
    sequenceToQuestionId,
    worksheetMutationData,
  ]);

  return streamingState === STATES.END;
};

export default useAiQuestionSaveToServer;
